import React, { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AppState, NativeEventEmitter, NativeModules, Platform } from 'react-native';
import { useIsFocused } from '@react-navigation/native';

const Context = createContext(null);
const native = Platform.OS === 'ios' ? NativeModules.MobileDuoHeader : null;
const symbols = {
  'pen-to-square': 'square.and.pencil', ghost: 'eye.slash', home: 'house',
  'magnifying-glass': 'magnifyingglass', 'table-columns': 'sidebar.right',
  'arrow-left': 'chevron.backward', plus: 'plus',
};

export function DuoHeaderProvider({ children, owner, dark = false }) {
  const focused = useIsFocused();
  const entries = useRef(new Map());
  const [revision, setRevision] = useState(0);
  const [active, setActive] = useState(false);
  useEffect(() => {
    if (!native) return undefined;
    const subscription = AppState.addEventListener('change', state => {
      if (state === 'active') setRevision(value => value + 1);
    });
    return () => subscription.remove();
  }, []);
  const register = useCallback((id, item, invoke) => {
    entries.current.set(id, { item: { ...item, id }, invoke });
    setRevision(value => value + 1);
    return () => { entries.current.delete(id); setRevision(value => value + 1); };
  }, []);
  useEffect(() => {
    if (!native || !focused) { setActive(false); return undefined; }
    let current = true;
    native.setItems(owner, [...entries.current.values()].map(entry => entry.item), dark)
      .then(installed => { if (current) setActive(installed === true); })
      .catch(() => { if (current) setActive(false); });
    return () => { current = false; };
  }, [focused, owner, revision, dark]);
  useEffect(() => {
    if (!native || !focused) return undefined;
    const listener = new NativeEventEmitter(native).addListener('onDuoHeaderAction', event => {
      if (event?.owner !== owner) return;
      if (event.capabilityChanged) { setRevision(value => value + 1); return; }
      if (typeof event.id !== 'string') return;
      for (const [id, entry] of entries.current) {
        if (event.id === id || event.id.startsWith(`${id}/`)) { entry.invoke(event.id); break; }
      }
    });
    return () => { listener.remove(); native.clear(owner); };
  }, [focused, owner]);
  const value = useMemo(() => ({ active, register }), [active, register]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useDuoHeaderActive() {
  return !!useContext(Context)?.active;
}

export function useDuoHeaderAction({ title, icon, systemImage, disabled, onPress }) {
  return useDuoHeaderItem({ title, symbol: systemImage || symbols[icon], disabled, onSelect: onPress });
}

export function useDuoHeaderItem(item) {
  const context = useContext(Context);
  const id = useId();
  const callbacks = useRef(new Map());
  const next = new Map();
  const serialize = (node, key) => {
    if (node.onSelect && !node.disabled) next.set(key, node.onSelect);
    return { id: key, title: node.title, symbol: node.symbol, disabled: !!node.disabled,
      destructive: !!node.destructive, children: node.children?.map((child, i) => serialize(child, `${key}/${i}`)) };
  };
  const value = serialize(item, id);
  useLayoutEffect(() => { callbacks.current = next; });
  const signature = JSON.stringify(value);
  const supported = !!(native && context && item.title && item.symbol && (item.onSelect || item.children?.length));
  useEffect(() => {
    if (!supported) return undefined;
    return context.register(id, JSON.parse(signature), key => callbacks.current.get(key)?.());
  }, [supported, context?.register, id, signature]);
  return supported && context.active;
}
